import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { emptyFilters } from "../config";
import { fetchStations, fetchStops, fetchTickets } from "../repo";
import { buildQuery, buildViewSearch, chinaToday, createFareSelection, dateTabs, filterTickets, initialDraft, initialLeg, isDate, moveFareSelection, queryWindow, resolveStation, retainAvailableFilters, sortTickets } from "../service";
import type { RailwayDraft, RailwayFareSelection, RailwayFilters, RailwayQuery, RailwaySeat, RailwaySort, RailwayStationsData, RailwayTrain } from "../types";

const stationsQueryKey = ["railway", "stations"] as const;

/** 编排车站元数据、已提交条件与车次查询。 */
export function useRailwayQuery(search = "") {
  const client = useQueryClient();
  const submitSequence = useRef(0);
  const [draft, setDraft] = useState(() => initialDraft(search));
  const [submitted, setSubmitted] = useState(() => ({ draft: initialDraft(search), leg: initialLeg(search), revision: 0 }));
  const [validationError, setValidationError] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState<RailwaySort>({ key: "depart", direction: "asc" });
  const [selectedFares, setSelectedFares] = useState<RailwayFareSelection[]>([]);
  const [stopTrain, setStopTrain] = useState<RailwayTrain | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const metadata = useQuery({ queryKey: stationsQueryKey, queryFn: ({ signal }) => fetchStations(signal), staleTime: 15 * 60_000, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const submission = useMemo(() => {
    if (!metadata.data) return { query: null, error: "" };
    const window = queryWindow(metadata.data, submitted.draft.purpose);
    try { return { query: buildQuery(submitted.draft, metadata.data.stations, window.min_date, window.max_date, submitted.leg), error: "" }; }
    catch (error) { return { query: null, error: `${error instanceof Error ? error.message : "查询条件无效"}；请在上方修改条件后点击查询，重新查询可刷新官方日期范围。` }; }
  }, [metadata.data, submitted]);
  const tickets = useQuery({
    queryKey: ["railway", "tickets", submission.query, submitted.revision],
    queryFn: async ({ signal }) => {
      const result = await fetchTickets(submission.query as RailwayQuery, signal);
      if (!signal.aborted) client.setQueryData<RailwayStationsData>(stationsQueryKey, (current) => current ? {
        ...current, ...(result.purpose === "0X00"
          ? { student_min_date: result.min_date, student_max_date: result.max_date, student_window_source: result.window_source }
          : { min_date: result.min_date, max_date: result.max_date, window_source: result.window_source }),
      } : current);
      return result;
    },
    enabled: !!submission.query, staleTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false,
  });
  const data = !tickets.isError && !tickets.isFetching && !submission.error ? tickets.data : undefined;
  useEffect(() => {
    if (data) setFilters((value) => retainAvailableFilters(value, data.trains));
  }, [data]);
  const trains = useMemo(() => sortTickets(filterTickets(data?.trains ?? [], filters), sort), [data, filters, sort]);
  const stops = useQuery({
    queryKey: ["railway", "stops", stopTrain?.train_start_date, stopTrain?.train_no, stopTrain?.origin_code, stopTrain?.destination_code],
    queryFn: ({ signal }) => fetchStops({ date: stopTrain!.train_start_date, from_station: stopTrain!.origin_code, to_station: stopTrain!.destination_code, train_no: stopTrain!.train_no }, signal),
    enabled: !!stopTrain && !!submission.query, staleTime: 5 * 60_000, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false,
  });

  /** 提交前校验车站与日期，输入过程不触发上游请求。 */
  async function submit(leg: "outbound" | "return" = submitted.leg, nextDraft = draft) {
    if (!metadata.data) return;
    const sequence = ++submitSequence.current;
    const nextLeg = nextDraft.roundTrip ? leg : "outbound";
    let currentMetadata = metadata.data;
    try {
      const cachedWindow = queryWindow(currentMetadata, nextDraft.purpose);
      const requestedDate = nextLeg === "return" ? nextDraft.returnDate : nextDraft.date;
      if (metadata.isStale || currentMetadata.min_date < chinaToday() || (isDate(requestedDate) && (requestedDate < cachedWindow.min_date || requestedDate > cachedWindow.max_date))) {
        const refreshed = await metadata.refetch({ cancelRefetch: false });
        if (sequence !== submitSequence.current) return;
        if (refreshed.isError || !refreshed.data) throw new Error("官方查询日期暂时无法刷新，请稍后重新查询");
        currentMetadata = refreshed.data;
      }
      const window = queryWindow(currentMetadata, nextDraft.purpose);
      const nextQuery = buildQuery(nextDraft, currentMetadata.stations, window.min_date, window.max_date, nextLeg);
      const sameRoute = nextQuery.from_station === submission.query?.from_station && nextQuery.to_station === submission.query?.to_station;
      setValidationError(""); setStopTrain(null);
      setFilters((value) => ({ ...value, seats: [], ...(sameRoute ? {} : { origins: [], destinations: [] }) }));
      setSubmitted((value) => ({ draft: { ...nextDraft }, leg: nextLeg, revision: value.revision + 1 }));
    } catch (error) { setValidationError(error instanceof Error ? error.message : "查询条件无效"); }
  }

  /** 日期标签总是沿用当前已提交线路，避免新标题混用旧车站。 */
  function selectDate(date: string) {
    const next = { ...submitted.draft, [submitted.leg === "return" ? "returnDate" : "date"]: date };
    if (next.roundTrip && next.returnDate < next.date) next.returnDate = next.date;
    setDraft(next); void submit(submitted.leg, next);
  }

  /** 对单个查询按用户意图发起重试。 */
  function retry() {
    if (metadata.isError || submission.error) void metadata.refetch();
    else if (submission.query) void submit(submitted.leg, submitted.draft);
  }

  /** 使用已呈现结果的日期及价格，编辑草稿不会改变选中方案。 */
  function toggleFare(train: RailwayTrain, seat: RailwaySeat, berthName: string | null = null) {
    if (!data) return;
    const selection = createFareSelection(data, train, seat, berthName);
    if (!selection) return;
    setSelectedFares((items) => items.some((item) => item.id === selection.id)
      ? items.filter((item) => item.id !== selection.id) : [...items, selection]);
  }

  const query = submission.query;
  const dateWindow = metadata.data ? queryWindow(metadata.data, submitted.draft.purpose) : undefined;
  const draftWindow = metadata.data ? queryWindow(metadata.data, draft.purpose) : undefined;
  const stationName = (code: string) => resolveStation(code, metadata.data?.stations ?? [])?.name ?? code;
  return {
    draft, data, query, trains, filters, sort, setSort, selectedFares, stopTrain, filtersOpen, validationError,
    metadata: metadata.data, dateWindow, draftWindow, stations: metadata.data?.stations ?? [],
    dates: dateWindow ? dateTabs(dateWindow.min_date, dateWindow.max_date, query?.date ?? submitted.draft.date) : [],
    fromName: query ? stationName(query.from_station) : submitted.draft.from,
    toName: query ? stationName(query.to_station) : submitted.draft.to,
    leg: submitted.leg, submittedDraft: submitted.draft, fullViewSearch: buildViewSearch(submitted.draft, submitted.leg),
    isLoading: metadata.isPending || metadata.isFetching || (!!query && tickets.isFetching),
    error: metadata.error?.message || submission.error || tickets.error?.message || "",
    stops: !stops.isError ? stops.data?.stops ?? [] : [], stopsLoading: !!stopTrain && stops.isPending, stopsError: stops.error?.message ?? "",
    updateDraft: (patch: Partial<RailwayDraft>) => { setDraft((value) => ({ ...value, ...patch })); setValidationError(""); },
    setFilters, setFiltersOpen, submit: (leg?: "outbound" | "return", nextDraft?: RailwayDraft) => { void submit(leg, nextDraft); }, selectDate, retry, setStopTrain,
    selectLeg: (leg: "outbound" | "return") => { void submit(leg, submitted.draft); },
    retryStops: () => { void stops.refetch(); },
    updateFilters: (patch: Partial<RailwayFilters>) => setFilters((value) => ({ ...value, ...patch })),
    swapStations: () => setDraft((value) => ({ ...value, from: value.to, to: value.from })),
    toggleFare,
    removeFare: (id: string) => setSelectedFares((items) => items.filter((item) => item.id !== id)),
    moveFare: (id: string, targetId: string) => setSelectedFares((items) => moveFareSelection(items, id, targetId)),
    toggleSort: (key: RailwaySort["key"]) => setSort((value) => ({ key, direction: value.key === key && value.direction === "asc" ? "desc" : "asc" })),
  };
}
